import { pdf } from '@react-pdf/renderer';

import { useFileEncryptionUpload } from '@/entities/file/hooks/useFileEncryptionUpload';
import { useNotices } from '@/features/notices/useNotices';
import { ApiVideoMetadataDto, useApi } from '@/shared/api';
import { getPersonFullName } from '@/shared/helpers/names';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { VideoCertificatePDF } from '@/shared/pdf-templates';
import { VideoCertificatePDFProps } from '@/shared/pdf-templates/VideoCertificatePDF/types';
import { sha256FromBlob } from '@/shared/utils/files';

import { useIwEditingStore, useIwExecutionStore, VideoAttachment } from '../stores';
import { useIwRecordingStore } from '../stores/iwRecording.store';

import { VIDEO_CONFIG } from './useWebcamRecorder/config';
import { useInterwillsActions } from './useInterwillActions';

const getCertificateTitle = (date: string) => `VIDEO VERIFICATION CERTIFICATE (${date}).pdf`;

export const useUploadVerificationRecordings = () => {
  const api = useApi();
  const actions = useInterwillsActions();
  const router = useAppRouter();
  const notices = useNotices();
  const { encryptAndUploadFile } = useFileEncryptionUpload();

  const recording = useIwRecordingStore();
  const execution = useIwExecutionStore();
  const editing = useIwEditingStore();
  const saveWillCertificate = async (willId: string, props: VideoCertificatePDFProps) => {
    const { data: allParticipants } = await api.willsGetParticipants(willId);
    const allBeneficiaries = await api.profilesGetBeneficiaries();

    const participants = allBeneficiaries.data?.filter(
      (item) => allParticipants?.some((participant) => participant.profileId === item.id),
    );

    const docPdf = pdf(<VideoCertificatePDF {...props} />);
    const blob = await docPdf.toBlob();
    const file = new File([blob], getCertificateTitle(props.date));
    const uploadFile = await encryptAndUploadFile(file, {
      publicKey: participants?.map((item) => item.publicKey ?? null) ?? [],
      itemData: { isFileAttached: true },
    });
    if (!uploadFile) return null;
    await api.willsAddAttachment(willId, {
      type: 'DOCUMENT',
      filePath: uploadFile,
    });

    return uploadFile;
  };

  const uploadRecordings = async () => {
    const resWill = await actions.getInterwill();
    if (!resWill.centralDbInterwillId) return;

    const jobs: { witnessId?: DtoId; url: string }[] = [];

    if (recording.testator?.url) {
      jobs.push({ url: recording.testator.url });
    }

    for (const witnessId of Object.keys(recording.witnesses)) {
      const w = recording.witnesses[witnessId];
      if (w?.url) jobs.push({ witnessId, url: w.url });
    }

    const newWitnesses = { ...execution.witnessVerifications };
    const newVideoCertificates = { ...execution.videoCertificates };

    await Promise.allSettled(
      jobs.map(async ({ witnessId, url }) => {
        const uploadRes = await api.willsAddAttachment(resWill.centralDbInterwillId!, {
          type: 'VIDEO',
          filePath: url,
        });
        if (!uploadRes.data) {
          notices.error(`Failed to attach a video`);
          return;
        }

        const attachment = {
          id: uploadRes.data.id,
          attachmentNumber: uploadRes.data.attachmentNumber,
          createdAt: uploadRes.data.createdAt,
          filePath: uploadRes.data.filePath,
          certificateId: uploadRes.data.certificateId,
        };
        const videoData = witnessId ? recording.witnesses[witnessId] : recording.testator;
        const decodedVideo = witnessId
          ? recording.witnessesDecoded[witnessId]
          : recording.testatorDecoded;
        if (!videoData || !decodedVideo || !attachment.filePath || !resWill.centralDbInterwillId)
          return;

        const hash = await sha256FromBlob(decodedVideo);
        const metadata: ApiVideoMetadataDto = {
          attachmentId: attachment.id,
          durationSeconds: videoData.duration,
          filePath: attachment.filePath,
          fileSizeBytes: decodedVideo.size,
          hasAudio: true,
          hash,
          recordingEndedAt: videoData.endedAt,
          videoCodec: VIDEO_CONFIG.videoCodec,
          audioCodec: VIDEO_CONFIG.audioCodec,
          willId: resWill.centralDbInterwillId,
          resolution: videoData.resolution ?? '',
          ...(videoData.frameRate && { frameRate: String(videoData.frameRate) }),
          ...(videoData.startedAt && { recordingStartedAt: videoData.startedAt }),
        };

        const certRes = await api.videoCertificateCreate(metadata);
        const certData = certRes.data;
        const certificateId = certRes.data?.id ? String(certRes.data.id) : '';

        if (!certData) {
          notices.error(`Failed to create video certificate.`);
          return;
        }
        newVideoCertificates[certData.attachmentId] = {
          ...certData,
        };

        await api.willsUpdateAttachment(resWill.centralDbInterwillId!, attachment.id, {
          certificateId,
        });

        const generatedPdfPath = await saveWillCertificate(resWill.centralDbInterwillId, {
          date: execution.createdDate ?? '',
          fullName: getPersonFullName(editing.testator),
          videoData: certData,
          willNumber: execution.willNumber ?? '',
          attachmentNumber: attachment.attachmentNumber,
        });

        if (!generatedPdfPath) return;

        const entry: VideoAttachment = {
          ...attachment,
          filePath: attachment.filePath,
          certificateId,
          duration: videoData.duration,
          recordingDate: videoData.endedAt,
          generatedPdfPath,
        };

        if (witnessId) {
          newWitnesses[witnessId] = entry;
        } else {
          execution.setTestatorVerification(entry);
        }
      }),
    );

    execution.setWitnessVerifications(newWitnesses);
    execution.setVideoCertificates(newVideoCertificates);
    recording.clear();
    router.push('/interwill');
  };

  return { uploadRecordings };
};
